import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import Sidebar from "./sidebar";
import CustomToolbar from "./CustomToolbarPrint";
import ProjectFilter from "./ProjectFilter";
import { genderProject, matchesProject } from "../utils/reportUtils";
import { db } from "../firebase";

const formatDate = (value) => {
  if (!value) return "";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  return value;
};

export default function CurrentlyInRehab() {
  const [employees, setEmployees] = useState([]);
  const [projectFilter, setProjectFilter] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const snapshot = await getDocs(collection(db, "employee"));
      const list = [];
      snapshot.forEach((document) => {
        list.push({ id: document.id, ...document.data() });
      });
      setEmployees(list);
      setLoading(false);
    };
    load();
  }, []);

  const allRows = useMemo(
    () =>
      employees
        .map((employee) => {
          const open = (Array.isArray(employee.rehab) ? employee.rehab : [])
            .filter((entry) => entry && entry.startDate && !entry.endDate)
            .sort((a, b) =>
              String(b.startDate).localeCompare(String(a.startDate))
            );
          if (open.length === 0) return null;
          return {
            ...employee,
            project: genderProject(employee.gender),
            rehabStartDate: open[0].startDate || "",
            rehabLocation: open[0].location || "",
          };
        })
        .filter(Boolean)
        .sort(
          (a, b) =>
            String(a.surName || "").localeCompare(String(b.surName || "")) ||
            String(a.firstName || "").localeCompare(String(b.firstName || ""))
        ),
    [employees]
  );

  const rows = useMemo(
    () =>
      projectFilter.length === 0
        ? allRows
        : allRows.filter((row) => matchesProject(row.gender, projectFilter)),
    [allRows, projectFilter]
  );

  const counts = useMemo(
    () => ({
      total: rows.length,
      moses: rows.filter((row) => row.project === "Moses").length,
      miriam: rows.filter((row) => row.project === "Miriam").length,
    }),
    [rows]
  );

  const columns = [
    { field: "firstName", headerName: "Name", width: 150 },
    { field: "surName", headerName: "Surname", width: 150 },
    { field: "project", headerName: "Project", width: 130 },
    {
      field: "rehabStartDate",
      headerName: "Rehab Start",
      width: 140,
      valueFormatter: (params) => formatDate(params.value),
    },
    { field: "rehabLocation", headerName: "Location", width: 170 },
    { field: "contact", headerName: "Contact Number", width: 150 },
    { field: "postCode", headerName: "Post Code", width: 130 },
    { field: "DOB", headerName: "Date of Birth", width: 140 },
  ];

  const handleRowClick = (params) => {
    navigate("/profile", {
      state: {
        employee: params.row,
        activeStep: 0,
      },
    });
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3 }}
        style={{ marginTop: "50px" }}
      >
        <div style={{ width: "100%" }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Currently in Rehab
          </Typography>

          <ProjectFilter
            value={projectFilter}
            onChange={setProjectFilter}
            required={false}
          />

          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            Total: {counts.total} · Moses: {counts.moses} · Miriam:{" "}
            {counts.miriam}
          </Typography>

          <div style={{ height: 640, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 25]}
              onRowDoubleClick={handleRowClick}
              components={{ Toolbar: CustomToolbar }}
            />
          </div>
        </div>
      </Box>
    </Box>
  );
}
